'use client';

import React, { useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useInView } from '@/lib/useInView';

gsap.registerPlugin(ScrollTrigger);

// Accordion component for mobile with smooth height animation
function MobileAccordion({ 
  title, 
  isOpen, 
  onToggle, 
  children 
}: { 
  title: React.ReactNode; 
  isOpen: boolean; 
  onToggle: () => void; 
  children: React.ReactNode 
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  useLayoutEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(isOpen ? contentHeight : 0);
      
      // Refresh ScrollTrigger after height change to recalculate positions
      setTimeout(() => {
        ScrollTrigger.refresh();
      }, 1200); // Match animation duration (1.2 seconds)
    }
  }, [isOpen]);

  return (
    <div className="md:hidden">
      <button
        onClick={(e) => {
          e.preventDefault();
          // Temporarily disabled
          // onToggle();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            // Temporarily disabled
            // onToggle();
          }
        }}
        className="mt-[4px] w-full text-left py-5 focus:outline-none"
        aria-expanded={isOpen}
        aria-controls={`accordion-content-${title}`}
        disabled
      >
        <span className="heading-3">{title}</span>
      </button>
      <div 
        id={`accordion-content-${title}`}
        style={{ height: `${height}px` }}
        className="overflow-hidden transition-all duration-[1200ms] ease-in-out"
      >
        <div ref={contentRef} className="">
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * ListAccordion - Our commitments section with desktop cards and mobile accordion
 * 
 * Features:
 * - Desktop layout with 5 commitment cards with scroll animations
 * - Mobile accordion layout with auto-open on scroll
 * - SVG path animations for decorative elements (circles, underlines, stars)
 */
export default function ListAccordion() {
  // Refs for desktop SVG animations
  const wellbeingRef = useRef<HTMLDivElement>(null);
  const underlineRef = useRef<SVGPathElement>(null);
  const belongingBRef = useRef<HTMLSpanElement>(null);
  const belongingBPathRef = useRef<SVGPathElement>(null);
  const impactRef = useRef<HTMLSpanElement>(null);
  const impactStarPathRef = useRef<SVGPathElement>(null);

  // Refs for mobile SVG animations
  const belongingBMobileRef = useRef<HTMLSpanElement>(null);
  const belongingBMobilePathRef = useRef<SVGPathElement>(null);
  const wellbeingMobileRef = useRef<HTMLDivElement>(null);
  const wellbeingMobileUnderlineRef = useRef<SVGPathElement>(null);
  const impactMobileRef = useRef<HTMLSpanElement>(null);
  const impactMobileStarPathRef = useRef<SVGPathElement>(null);

  // Refs for scroll-triggered animations
  const ourCommitmentsHeadingRef = useInView<HTMLHeadingElement>();
  const commitmentCard1Ref = useInView<HTMLDivElement>();
  const commitmentCard2Ref = useInView<HTMLDivElement>();
  const commitmentCard3Ref = useInView<HTMLDivElement>();
  const commitmentCard4Ref = useInView<HTMLDivElement>();
  const commitmentCard5Ref = useInView<HTMLDivElement>();

  // Refs for mobile accordion sections (with useInView for animations)
  const belongingAccordionRef = useInView<HTMLDivElement>();
  const careerAccordionRef = useInView<HTMLDivElement>();
  const wellbeingAccordionRef = useInView<HTMLDivElement>();
  const learningAccordionRef = useInView<HTMLDivElement>();
  const impactAccordionRef = useInView<HTMLDivElement>();

  // State for accordion open/close
  // TEMPORARY: All sections open by default on mobile (disable ScrollTrigger auto-open)
  const ENABLE_SCROLL_TRIGGER = false; // Set to true to re-enable ScrollTrigger auto-open
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({});
  
  // Open all sections on mobile when ScrollTrigger is disabled
  useLayoutEffect(() => {
    if (!ENABLE_SCROLL_TRIGGER && typeof window !== 'undefined' && window.innerWidth <= 768) {
      setOpenSections({
        belonging: true,
        career: true,
        wellbeing: true,
        learning: true,
        impact: true
      });
    }
  }, []);

  const toggleSection = (sectionKey: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const openSectionOnScroll = (sectionKey: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionKey]: true
    }));
  };

  // Animate underline when Wellbeing heading scrolls into view
  useLayoutEffect(() => {
    if (!underlineRef.current) return;

    const path = underlineRef.current;
    const pathLength = path.getTotalLength();
    
    // Set up the initial state
    gsap.set(path, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
    });

    const context = gsap.context(() => {
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 1.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: wellbeingRef.current,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      });
    });

    return () => context.revert();
  }, []);

  // Animate circle around "B" in Belonging - hand-drawn stroke effect
  useLayoutEffect(() => {
    if (!belongingBPathRef.current) return;

    const path = belongingBPathRef.current;
    const pathLength = path.getTotalLength();

    // Set up stroke for drawing animation - stroke only
    gsap.set(path, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
      fill: 'none',
      stroke: '#E60000',
      strokeWidth: 2.5,
      strokeLinecap: 'round',
    });

    const context = gsap.context(() => {
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 1.5,
        ease: 'power1.inOut',
        scrollTrigger: {
          trigger: belongingBRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => context.revert();
  }, []);

  // Animate star after "Impact" - hand-drawn stroke effect
  useLayoutEffect(() => {
    if (!impactStarPathRef.current) return;

    const path = impactStarPathRef.current;
    const pathLength = path.getTotalLength();

    gsap.set(path, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
      fill: 'none',
      stroke: '#E60000',
      strokeWidth: 1.5,
    });

    const context = gsap.context(() => {
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 1.5,
        ease: 'power1.inOut',
        scrollTrigger: {
          trigger: impactRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => context.revert();
  }, []);

  // Animate circle around "B" in Belonging (Mobile) - hand-drawn stroke effect
  useLayoutEffect(() => {
    if (!belongingBMobilePathRef.current) return;

    const path = belongingBMobilePathRef.current;
    const pathLength = path.getTotalLength();

    gsap.set(path, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
      fill: 'none',
      stroke: '#E60000',
      strokeWidth: 2.5,
      strokeLinecap: 'round',
    });

    const context = gsap.context(() => {
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 1.5,
        ease: 'power1.inOut',
        scrollTrigger: {
          trigger: belongingBMobileRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => context.revert();
  }, []);

  // Animate star after "Impact" (Mobile) - hand-drawn stroke effect
  useLayoutEffect(() => {
    if (!impactMobileStarPathRef.current) return;

    const path = impactMobileStarPathRef.current;
    const pathLength = path.getTotalLength();

    gsap.set(path, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
      fill: 'none',
      stroke: '#E60000',
      strokeWidth: 1.5,
    });

    const context = gsap.context(() => {
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 1.5,
        ease: 'power1.inOut',
        scrollTrigger: {
          trigger: impactMobileRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => context.revert();
  }, []);

  // Animate underline for "Wellbeing" (Mobile) when it scrolls into view
  useLayoutEffect(() => {
    if (!wellbeingMobileUnderlineRef.current) return;

    const path = wellbeingMobileUnderlineRef.current;
    const pathLength = path.getTotalLength();
    
    // Set up the initial state
    gsap.set(path, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
    });

    const context = gsap.context(() => {
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 1.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: wellbeingMobileRef.current,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      });
    });

    return () => context.revert();
  }, []);

  // Auto-open mobile accordion sections on scroll - each section opens individually
  // TEMPORARY: Disabled - set ENABLE_SCROLL_TRIGGER to true to re-enable
  useLayoutEffect(() => {
    // Skip ScrollTrigger setup if disabled
    if (!ENABLE_SCROLL_TRIGGER) return;

    const accordionRefs = [
      { ref: belongingAccordionRef, key: 'belonging' },
      { ref: careerAccordionRef, key: 'career' },
      { ref: wellbeingAccordionRef, key: 'wellbeing' },
      { ref: learningAccordionRef, key: 'learning' },
      { ref: impactAccordionRef, key: 'impact' }
    ];

    const mm = gsap.matchMedia();

    mm.add('(max-width: 768px)', () => {
      const triggers: ScrollTrigger[] = [];

      accordionRefs.forEach(({ ref, key }, index) => {
        if (!ref.ref.current) return;

        const trigger = ScrollTrigger.create({
          trigger: ref.ref.current,
          start: 'top 50%', // Trigger when section reaches halfway up the screen
          end: 'bottom 40%', // End trigger zone
          onEnter: () => {
            openSectionOnScroll(key);
            // Refresh all triggers after opening to recalculate positions
            setTimeout(() => {
              ScrollTrigger.refresh();
            }, 50);
          },
          once: true, // Only trigger once per section
          invalidateOnRefresh: true, // Recalculate on layout changes
          markers: false, // Set to true for debugging
        });

        triggers.push(trigger);
      });

      return () => {
        triggers.forEach(trigger => trigger.kill());
      };
    });

    return () => mm.revert();
  }, []);

  return (
    <div className="accordion-pinning mb-16 global-padding">
      <h2 ref={ourCommitmentsHeadingRef.ref} className="pb-4 -mb-[5px] lg:mb-[1.1875rem] heading-2 border-b border-black animate-in-view">Our commitments</h2>
      
      {/* Desktop Layout */}
      <div className="commitments-desktop hidden md:block">
        <div ref={commitmentCard1Ref.ref} className="commitments-desktop-card1 flex flex-col md:flex-row md:items-start gap-6 py-4 md:p-6 border-b border-black animate-in-view mt-[3.5625rem]">
          <div className="md:w-1/2">
            <h3 className="body-xl-display">
              <span ref={belongingBRef} className="relative inline-block">
                <span className="relative z-[1]">Belonging</span>
                <svg
                  className="absolute left-[0.625rem] top-1/2 -translate-x-1/2 -translate-y-1/2 w-[4.375rem] h-[4.375rem] md:w-[7.125rem] md:h-[7.25rem] pointer-events-none z-0"
                  width="114"
                  height="116"
                  viewBox="0 0 114 116"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <g clipPath="url(#clip0_7439_44520)">
                    <path
                      ref={belongingBPathRef}
                      d="M32.7073 19.3796C33.6692 19.3403 34.3064 20.6951 33.6809 21.4226C31.3176 24.0213 29.0798 26.7306 26.8474 29.4421C21.2669 36.4 15.6597 44.2464 15.2344 53.4608C13.8651 76.3227 31.9546 100.596 56.0286 100.098C67.7199 101.192 78.1605 93.918 86.1397 86.1252C94.2697 77.8952 99.2961 66.4363 98.0602 54.776C97.616 36.5614 82.388 17.6702 63.0173 19.5952C58.4831 20.2183 54.5219 22.6476 50.7172 25.0093C49.1823 25.5399 46.2329 29.0966 45.0423 26.783C44.8151 26.2209 44.9556 25.4476 45.5725 25.1983C47.2498 24.2652 48.8355 23.172 50.4817 22.1843C59.584 15.5724 71.0508 15.4664 80.5936 21.2438C96.0144 29.8232 102.594 49.0695 99.7045 65.904C97.9206 75.2196 92.5566 83.5679 85.6611 89.9688C75.0339 99.4005 65.2473 104.386 50.7705 102.093C29.4149 99.7283 14.0212 78.396 13.0834 57.8279C11.9881 47.2036 17.2752 37.4443 23.7992 29.4468C26.7007 26.1155 29.2656 22.0514 32.7057 19.3758L32.7073 19.3796Z"
                      fill="#E60000"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_7439_44520">
                      <rect width="87" height="89" fill="white" transform="translate(114.009 82.5168) rotate(157.996)"/>
                    </clipPath>
                  </defs>
                </svg>
              </span>
            </h3>
          </div>
          <div className="md:w-1/2">
            <p className="body-default pb-[0.375rem]">We welcome all and celebrate difference because it makes us stronger. With programs like &ldquo;Havas All In&rdquo; we empower teams to own initiatives while collaborating globally.</p>
          </div>
        </div>
        <div ref={commitmentCard2Ref.ref} className="commitments-desktop-card2 flex flex-col md:flex-row md:items-start gap-6 py-4 md:p-6 border-b border-black animate-in-view mt-[3.5625rem]">
          <div className="md:w-1/2">
            <h3 className="body-xl-display">Career</h3>
          </div>
          <div className="md:w-1/2">
            <p className="body-default pb-[0.375rem]">We offer careers focused on growth, with programs like Femme Forward supporting women&rsquo;s career progression, and room to explore new roles in our global network.</p>
          </div>
        </div>
        <div ref={commitmentCard3Ref.ref} className="commitments-desktop-card3 flex flex-col md:flex-row md:items-start gap-6 py-4 md:p-6 border-b border-black animate-in-view mt-[3.5625rem]">
          <div className="md:w-1/2">
            <div ref={wellbeingRef} className="relative inline-block">
              <h3 className="body-xl-display">Wellbeing</h3>
              <svg 
                className="absolute -bottom-4 left-0 w-full h-auto max-w-[13.5rem]" 
                width="216" 
                height="178" 
                viewBox="0 0 216 20" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <g clipPath="url(#clip0_8500_40817)">
                  <path 
                    ref={underlineRef}
                    d="M211.729 85.6411C212.706 85.8024 213.501 86.0452 214.027 86.4433C214.5 86.8022 214.787 87.3101 214.733 88.1446C214.452 88.9793 213.983 89.3796 213.409 89.5655C212.781 89.7692 211.971 89.7338 211.025 89.5574C210.082 89.3815 209.091 89.0857 208.094 88.8217C207.141 88.5694 206.17 88.3443 205.33 88.347C165.348 82.7296 124.94 83.4219 84.8877 83.1818L84.8768 83.1817C68.4835 83.4371 52.0814 83.8145 35.7599 85.1541L35.7498 85.1544L35.7402 85.1561C33.2074 85.4658 30.5942 85.6578 27.9456 85.8397C25.3011 86.0214 22.619 86.1929 19.9664 86.4618C14.6624 86.9995 9.4285 87.9298 4.73048 90.1394L4.58571 90.2079L4.50796 90.3469C4.20537 90.8833 3.81412 91.1479 3.43159 91.2474C3.03971 91.3493 2.61371 91.2884 2.23763 91.0959C1.82731 90.8858 1.49825 90.5302 1.34772 90.1061C1.20136 89.6936 1.21117 89.1839 1.53033 88.622L1.53521 88.6146L1.53877 88.6077C2.05414 87.6248 3.38273 86.6831 5.51942 85.8247C7.62847 84.9775 10.4174 84.252 13.6987 83.6331C20.2579 82.396 28.7034 81.5967 37.425 81.0609C46.1431 80.5253 55.1211 80.2543 62.7389 80.0702C70.3246 79.8869 76.5804 79.7892 79.8653 79.6027C121.84 79.6402 164.095 79.2232 205.905 84.7982C206.676 85.088 207.663 85.216 208.65 85.3115C209.695 85.4127 210.755 85.4806 211.729 85.6411Z" 
                    fill="#E60000" 
                    stroke="#FDFEF6"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_8500_40817">
                    <rect width="190.603" height="101.682" fill="white" transform="translate(46.332) rotate(27.1066)"/>
                  </clipPath>
                </defs>
              </svg>
            </div>
          </div>
          <div className="md:w-1/2">
            <p className="body-default pb-[0.375rem]">We value health and wellbeing through ongoing learning that respects boundaries and encourages self-care, supported by programs like Havas Minds.</p>
          </div>
        </div>
        <div ref={commitmentCard4Ref.ref} className="commitments-desktop-card4 flex flex-col md:flex-row md:items-start gap-6 py-4 md:p-6 border-b border-black animate-in-view mt-[3.5625rem]">
          <div className="md:w-1/2">
            <h3 className="body-xl-display">Learning</h3>
          </div>
          <div className="md:w-1/2">
            <p className="body-default pb-[0.375rem]">We nurture a culture where questions and curiosity are encouraged, with platforms that advance skills and sustain growth, including Havas NextGen and Havas University.</p>
          </div>
        </div>
        <div ref={commitmentCard5Ref.ref} className="commitments-desktop-card5 flex flex-col md:flex-row md:items-start gap-6 py-4 md:p-6 border-b border-black animate-in-view mt-[3.5625rem]">
          <div className="md:w-1/2">
            <h3 className="body-xl-display">
              <span ref={impactRef} className="relative inline-block">Impact
                <svg
                  className="absolute -right-[2.1875rem] top-[1.25rem] -translate-y-1/2 w-[1.25rem] h-[1.4375rem] md:w-[1.6875rem] md:h-[1.9375rem] pointer-events-none"
                  width="27"
                  height="31"
                  viewBox="0 0 27 31"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <g clipPath="url(#clip0_8500_40821_desktop)">
                    <path
                      ref={impactStarPathRef}
                      d="M15.7988 0.624817C16.0364 0.617153 16.2608 0.731302 16.3916 0.948059C16.5144 1.1517 16.5307 1.40325 16.4814 1.65509V1.659C15.6538 5.65916 15.1698 9.55918 14.8691 13.3826C17.8628 11.5685 20.8675 9.71804 24.0371 8.17755L24.0742 8.159L24.1143 8.14923C24.1594 8.13856 24.2463 8.09718 24.3965 8.00568C24.5242 7.92786 24.7004 7.8122 24.8613 7.72638C25.0171 7.64329 25.2306 7.5465 25.4531 7.54572C25.5744 7.54535 25.7063 7.57325 25.8281 7.65118C25.9185 7.70908 25.9889 7.78584 26.0439 7.87189L26.0947 7.96075L26.1006 7.97443L26.1064 7.98907C26.1716 8.15593 26.1776 8.34564 26.1318 8.51447C26.0856 8.68477 25.9746 8.87282 25.7676 8.97833L25.7607 8.98126L25.7539 8.98517C22.8847 10.3043 20.1568 11.8982 17.4375 13.5408C20.1893 13.279 22.9716 13.1832 25.7432 13.7078L25.7422 13.7088C26.1268 13.7711 26.3235 14.0766 26.3652 14.3611C26.4052 14.6345 26.3194 14.9586 26.0771 15.1678L26.0537 15.1873L26.0273 15.2029C25.8082 15.3371 25.5471 15.363 25.3223 15.3582C25.0898 15.3532 24.8384 15.3133 24.6035 15.2684C24.4849 15.2457 24.365 15.2209 24.251 15.1971C24.1357 15.173 24.0259 15.1504 23.9209 15.1307C23.705 15.0901 23.5364 15.0679 23.4141 15.0711L23.3887 15.0721L23.3633 15.0692C20.8013 14.7927 18.2347 15.1183 15.625 15.4051C18.1116 18.2012 20.6648 20.9842 22.9111 24.0193L22.9365 24.0526L22.9531 24.0906C22.9723 24.1341 23.0298 24.2122 23.1475 24.3436C23.2475 24.4552 23.3928 24.608 23.5068 24.7508C23.6177 24.8896 23.7519 25.0808 23.7949 25.2986C23.8183 25.417 23.8163 25.5502 23.7646 25.6844C23.7134 25.8177 23.6237 25.9243 23.5156 26.0076L23.5039 26.0174L23.4922 26.0252C23.3411 26.1239 23.1551 26.1678 22.9795 26.1551C22.8015 26.1421 22.5954 26.0662 22.4541 25.8807L22.4463 25.8699C20.1044 22.5595 17.3779 19.5667 14.6611 16.5262C14.5287 18.9205 14.4518 21.2878 14.3955 23.6336C14.3764 25.1637 14.3372 26.6871 14.3682 28.1688L14.3848 28.5233C14.389 28.6522 14.391 28.7896 14.3867 28.9266C14.3784 29.1947 14.3464 29.4976 14.2461 29.7547V29.7557C14.18 29.9243 14.0728 30.1009 13.8965 30.2244C13.7121 30.3535 13.4921 30.3963 13.2588 30.3651L13.126 30.3475L13.0342 30.2488C12.9082 30.1136 12.8379 29.9334 12.793 29.7703C12.7458 29.5988 12.7147 29.3998 12.6943 29.1902C12.6535 28.7707 12.6503 28.2625 12.6602 27.7488C12.6805 26.6908 12.7534 25.6355 12.7158 25.0496L12.7148 25.0359V25.0213C12.7472 22.1196 12.8734 19.1668 13.0645 16.1795C12.9958 16.2203 12.9273 16.2617 12.8584 16.3026L12.8555 16.3045C10.195 17.8488 7.51042 19.2516 4.94824 20.8973C4.6785 21.0944 4.39684 21.2853 4.12207 21.4725C3.83852 21.6656 3.56071 21.857 3.29492 22.0574C2.8142 22.4199 2.38933 22.8027 2.06738 23.2557C2.10643 23.4504 2.06518 23.6413 1.98145 23.7908C1.86928 23.9911 1.64378 24.1852 1.35156 24.1854C1.09794 24.1854 0.891432 24.0374 0.764648 23.8221L0.757812 23.8113L1.08789 23.6326L0.758789 23.8104C0.538319 23.4028 0.614858 22.9652 0.813477 22.575C1.00998 22.1891 1.34941 21.7951 1.76465 21.407C2.59922 20.6271 3.83063 19.7853 5.14844 18.9764C6.47082 18.1647 7.90137 17.3732 9.14551 16.6951C9.59552 16.4499 10.0189 16.2196 10.4043 16.0096C8.52208 16.2402 6.64111 16.4822 4.76074 16.7322C4.76207 16.8917 4.72393 17.0455 4.66504 17.1668C4.52603 17.453 4.17989 17.7706 3.71582 17.6404L3.70605 17.6375L3.69727 17.6346C3.42049 17.5409 3.02595 17.4407 2.69336 17.283L2.69043 17.282C2.60725 17.2418 2.51648 17.1947 2.42676 17.1375L2.37402 17.1033L2.33398 17.0535C2.16112 16.8347 2.04519 16.5888 2.05859 16.325C2.0726 16.0511 2.22088 15.843 2.39648 15.6971C2.72251 15.4262 3.25257 15.2755 3.75293 15.1766C4.27429 15.0735 4.85194 15.0125 5.33398 14.9647C5.57835 14.9404 5.79529 14.92 5.97656 14.8992C6.1637 14.8778 6.28634 14.8587 6.35059 14.8416L6.37305 14.8358L6.39648 14.8318C8.05543 14.6073 9.72217 14.4213 11.376 14.2186L11.3828 14.2176C11.7612 14.1792 12.1399 14.1338 12.5195 14.0906C10.6351 11.8777 8.84953 9.62974 6.87598 7.53107C6.63127 7.30049 6.39023 7.05686 6.1543 6.81915C5.91229 6.57532 5.67346 6.33702 5.42773 6.11212C4.9842 5.70619 4.53231 5.35886 4.03223 5.12677C3.8501 5.20213 3.65616 5.19847 3.49414 5.14435C3.27448 5.07093 3.04436 4.88355 2.98926 4.59747V4.5965C2.94081 4.34909 3.04378 4.11906 3.22949 3.95294L3.23828 3.94513C3.59471 3.64562 4.03959 3.63778 4.45898 3.76056C4.87328 3.88192 5.32205 4.14449 5.7793 4.48224C6.69824 5.16102 7.7532 6.22143 8.79297 7.37384C9.83636 8.53027 10.8806 9.79772 11.7783 10.9012C12.3658 11.6233 12.8878 12.2687 13.3066 12.7723C13.3574 12.1301 13.4096 11.486 13.4658 10.8406L13.4668 10.8328C13.7943 7.79146 14.2509 4.70539 14.8857 1.57013L14.8867 1.56329C14.9998 1.04859 15.3476 0.639578 15.7988 0.624817Z"
                      fill="#E60000"
                      stroke="#E60000"
                      strokeWidth="0.75"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_8500_40821_desktop">
                      <rect width="27" height="31" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
              </span>
            </h3>
          </div>
          <div className="md:w-1/2">
            <p className="body-default pb-[0.375rem]">
              We work together to drive meaningful results, taking pride in work with real value and purpose, through programs like Impact+, our sustainable development strategy.
            </p>
          </div>
        </div>
      </div>

      {/* Mobile Accordion Layout */}
      <div className="mobile-accordion-container md:hidden space-y-0">
        <div ref={belongingAccordionRef.ref} className="animate-in-view">
          <MobileAccordion
            title={
              <>
                <span className="mt-[4px] relative inline-block">
                  <span ref={belongingBMobileRef} className="relative inline-block">
                    <span className="relative z-[1]">B</span>
                    <svg
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[4.375rem] h-[4.375rem] pointer-events-none z-0"
                      width="114"
                      height="116"
                      viewBox="0 0 114 116"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <g clipPath="url(#clip0_7439_44520_mobile)">
                        <path
                          ref={belongingBMobilePathRef}
                          d="M32.7073 19.3796C33.6692 19.3403 34.3064 20.6951 33.6809 21.4226C31.3176 24.0213 29.0798 26.7306 26.8474 29.4421C21.2669 36.4 15.6597 44.2464 15.2344 53.4608C13.8651 76.3227 31.9546 100.596 56.0286 100.098C67.7199 101.192 78.1605 93.918 86.1397 86.1252C94.2697 77.8952 99.2961 66.4363 98.0602 54.776C97.616 36.5614 82.388 17.6702 63.0173 19.5952C58.4831 20.2183 54.5219 22.6476 50.7172 25.0093C49.1823 25.5399 46.2329 29.0966 45.0423 26.783C44.8151 26.2209 44.9556 25.4476 45.5725 25.1983C47.2498 24.2652 48.8355 23.172 50.4817 22.1843C59.584 15.5724 71.0508 15.4664 80.5936 21.2438C96.0144 29.8232 102.594 49.0695 99.7045 65.904C97.9206 75.2196 92.5566 83.5679 85.6611 89.9688C75.0339 99.4005 65.2473 104.386 50.7705 102.093C29.4149 99.7283 14.0212 78.396 13.0834 57.8279C11.9881 47.2036 17.2752 37.4443 23.7992 29.4468C26.7007 26.1155 29.2656 22.0514 32.7057 19.3758L32.7073 19.3796Z"
                          fill="#E60000"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_7439_44520_mobile">
                          <rect width="87" height="89" fill="white" transform="translate(114.009 82.5168) rotate(157.996)"/>
                        </clipPath>
                      </defs>
                    </svg>
                  </span>
                  <span>elonging</span>
                </span>
              </>
            }
            isOpen={openSections.belonging || false}
            onToggle={() => toggleSection('belonging')}
          >
            <p className="body-default pb-[2.5625rem]">We welcome all and celebrate difference because it makes us stronger. With programs like &ldquo;Havas All In&rdquo; we empower teams to own initiatives while collaborating globally.</p>
          </MobileAccordion>
        </div>
        
        <div ref={careerAccordionRef.ref} className="border-t border-black animate-in-view">
          <MobileAccordion
            title="Career"
            isOpen={openSections.career || false}
            onToggle={() => toggleSection('career')}
          >
            <p className="body-default pb-[2.5625rem]">We offer careers focused on growth, with programs like Femme Forward supporting women&rsquo;s career progression, and room to explore new roles in our global network.</p>
          </MobileAccordion>
        </div>
        
        <div ref={wellbeingAccordionRef.ref} className="border-t border-black animate-in-view">
          <MobileAccordion
            title={
              <div ref={wellbeingMobileRef} className="relative inline-block">
                Wellbeing
                <svg 
                  className="absolute -bottom-4 left-0 w-full h-auto max-w-[13.5rem]" 
                  width="216" 
                  height="178" 
                  viewBox="0 0 216 20" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <g clipPath="url(#clip0_8500_40817_mobile)">
                    <path 
                      ref={wellbeingMobileUnderlineRef}
                      d="M211.729 85.6411C212.706 85.8024 213.501 86.0452 214.027 86.4433C214.5 86.8022 214.787 87.3101 214.733 88.1446C214.452 88.9793 213.983 89.3796 213.409 89.5655C212.781 89.7692 211.971 89.7338 211.025 89.5574C210.082 89.3815 209.091 89.0857 208.094 88.8217C207.141 88.5694 206.17 88.3443 205.33 88.347C165.348 82.7296 124.94 83.4219 84.8877 83.1818L84.8768 83.1817C68.4835 83.4371 52.0814 83.8145 35.7599 85.1541L35.7498 85.1544L35.7402 85.1561C33.2074 85.4658 30.5942 85.6578 27.9456 85.8397C25.3011 86.0214 22.619 86.1929 19.9664 86.4618C14.6624 86.9995 9.4285 87.9298 4.73048 90.1394L4.58571 90.2079L4.50796 90.3469C4.20537 90.8833 3.81412 91.1479 3.43159 91.2474C3.03971 91.3493 2.61371 91.2884 2.23763 91.0959C1.82731 90.8858 1.49825 90.5302 1.34772 90.1061C1.20136 89.6936 1.21117 89.1839 1.53033 88.622L1.53521 88.6146L1.53877 88.6077C2.05414 87.6248 3.38273 86.6831 5.51942 85.8247C7.62847 84.9775 10.4174 84.252 13.6987 83.6331C20.2579 82.396 28.7034 81.5967 37.425 81.0609C46.1431 80.5253 55.1211 80.2543 62.7389 80.0702C70.3246 79.8869 76.5804 79.7892 79.8653 79.6027C121.84 79.6402 164.095 79.2232 205.905 84.7982C206.676 85.088 207.663 85.216 208.65 85.3115C209.695 85.4127 210.755 85.4806 211.729 85.6411Z" 
                      fill="#E60000" 
                      stroke="#FDFEF6"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_8500_40817_mobile">
                      <rect width="190.603" height="101.682" fill="white" transform="translate(46.332) rotate(27.1066)"/>
                    </clipPath>
                  </defs>
                </svg>
              </div>
            }
            isOpen={openSections.wellbeing || false}
            onToggle={() => toggleSection('wellbeing')}
          >
            <p className="body-default pb-[2.5625rem]">We value health and wellbeing through ongoing learning that respects boundaries and encourages self-care, supported by programs like Havas Minds.</p>
          </MobileAccordion>
        </div>
        
        <div ref={learningAccordionRef.ref} className="border-t border-black animate-in-view">
          <MobileAccordion
            title="Learning"
            isOpen={openSections.learning || false}
            onToggle={() => toggleSection('learning')}
          >
            <p className="body-default pb-[2.5625rem]">We nurture a culture where questions and curiosity are encouraged, with platforms that advance skills and sustain growth, including Havas NextGen and Havas University.</p>
          </MobileAccordion>
        </div>
        
        <div ref={impactAccordionRef.ref} className="border-t border-black animate-in-view">
          <MobileAccordion
            title={
              <>
                <span ref={impactMobileRef} className="relative inline-block">Impact
                  <svg
                    className="absolute -right-[1.5625rem] top-[calc(50%-0.625rem)] -translate-y-1/2 w-[1.25rem] h-[1.4375rem] pointer-events-none"
                    width="27"
                    height="31"
                    viewBox="0 0 27 31"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <g clipPath="url(#clip0_8500_40821)">
                      <path
                        ref={impactMobileStarPathRef}
                        d="M15.7988 0.624817C16.0364 0.617153 16.2608 0.731302 16.3916 0.948059C16.5144 1.1517 16.5307 1.40325 16.4814 1.65509V1.659C15.6538 5.65916 15.1698 9.55918 14.8691 13.3826C17.8628 11.5685 20.8675 9.71804 24.0371 8.17755L24.0742 8.159L24.1143 8.14923C24.1594 8.13856 24.2463 8.09718 24.3965 8.00568C24.5242 7.92786 24.7004 7.8122 24.8613 7.72638C25.0171 7.64329 25.2306 7.5465 25.4531 7.54572C25.5744 7.54535 25.7063 7.57325 25.8281 7.65118C25.9185 7.70908 25.9889 7.78584 26.0439 7.87189L26.0947 7.96075L26.1006 7.97443L26.1064 7.98907C26.1716 8.15593 26.1776 8.34564 26.1318 8.51447C26.0856 8.68477 25.9746 8.87282 25.7676 8.97833L25.7607 8.98126L25.7539 8.98517C22.8847 10.3043 20.1568 11.8982 17.4375 13.5408C20.1893 13.279 22.9716 13.1832 25.7432 13.7078L25.7422 13.7088C26.1268 13.7711 26.3235 14.0766 26.3652 14.3611C26.4052 14.6345 26.3194 14.9586 26.0771 15.1678L26.0537 15.1873L26.0273 15.2029C25.8082 15.3371 25.5471 15.363 25.3223 15.3582C25.0898 15.3532 24.8384 15.3133 24.6035 15.2684C24.4849 15.2457 24.365 15.2209 24.251 15.1971C24.1357 15.173 24.0259 15.1504 23.9209 15.1307C23.705 15.0901 23.5364 15.0679 23.4141 15.0711L23.3887 15.0721L23.3633 15.0692C20.8013 14.7927 18.2347 15.1183 15.625 15.4051C18.1116 18.2012 20.6648 20.9842 22.9111 24.0193L22.9365 24.0526L22.9531 24.0906C22.9723 24.1341 23.0298 24.2122 23.1475 24.3436C23.2475 24.4552 23.3928 24.608 23.5068 24.7508C23.6177 24.8896 23.7519 25.0808 23.7949 25.2986C23.8183 25.417 23.8163 25.5502 23.7646 25.6844C23.7134 25.8177 23.6237 25.9243 23.5156 26.0076L23.5039 26.0174L23.4922 26.0252C23.3411 26.1239 23.1551 26.1678 22.9795 26.1551C22.8015 26.1421 22.5954 26.0662 22.4541 25.8807L22.4463 25.8699C20.1044 22.5595 17.3779 19.5667 14.6611 16.5262C14.5287 18.9205 14.4518 21.2878 14.3955 23.6336C14.3764 25.1637 14.3372 26.6871 14.3682 28.1688L14.3848 28.5233C14.389 28.6522 14.391 28.7896 14.3867 28.9266C14.3784 29.1947 14.3464 29.4976 14.2461 29.7547V29.7557C14.18 29.9243 14.0728 30.1009 13.8965 30.2244C13.7121 30.3535 13.4921 30.3963 13.2588 30.3651L13.126 30.3475L13.0342 30.2488C12.9082 30.1136 12.8379 29.9334 12.793 29.7703C12.7458 29.5988 12.7147 29.3998 12.6943 29.1902C12.6535 28.7707 12.6503 28.2625 12.6602 27.7488C12.6805 26.6908 12.7534 25.6355 12.7158 25.0496L12.7148 25.0359V25.0213C12.7472 22.1196 12.8734 19.1668 13.0645 16.1795C12.9958 16.2203 12.9273 16.2617 12.8584 16.3026L12.8555 16.3045C10.195 17.8488 7.51042 19.2516 4.94824 20.8973C4.6785 21.0944 4.39684 21.2853 4.12207 21.4725C3.83852 21.6656 3.56071 21.857 3.29492 22.0574C2.8142 22.4199 2.38933 22.8027 2.06738 23.2557C2.10643 23.4504 2.06518 23.6413 1.98145 23.7908C1.86928 23.9911 1.64378 24.1852 1.35156 24.1854C1.09794 24.1854 0.891432 24.0374 0.764648 23.8221L0.757812 23.8113L1.08789 23.6326L0.758789 23.8104C0.538319 23.4028 0.614858 22.9652 0.813477 22.575C1.00998 22.1891 1.34941 21.7951 1.76465 21.407C2.59922 20.6271 3.83063 19.7853 5.14844 18.9764C6.47082 18.1647 7.90137 17.3732 9.14551 16.6951C9.59552 16.4499 10.0189 16.2196 10.4043 16.0096C8.52208 16.2402 6.64111 16.4822 4.76074 16.7322C4.76207 16.8917 4.72393 17.0455 4.66504 17.1668C4.52603 17.453 4.17989 17.7706 3.71582 17.6404L3.70605 17.6375L3.69727 17.6346C3.42049 17.5409 3.02595 17.4407 2.69336 17.283L2.69043 17.282C2.60725 17.2418 2.51648 17.1947 2.42676 17.1375L2.37402 17.1033L2.33398 17.0535C2.16112 16.8347 2.04519 16.5888 2.05859 16.325C2.0726 16.0511 2.22088 15.843 2.39648 15.6971C2.72251 15.4262 3.25257 15.2755 3.75293 15.1766C4.27429 15.0735 4.85194 15.0125 5.33398 14.9647C5.57835 14.9404 5.79529 14.92 5.97656 14.8992C6.1637 14.8778 6.28634 14.8587 6.35059 14.8416L6.37305 14.8358L6.39648 14.8318C8.05543 14.6073 9.72217 14.4213 11.376 14.2186L11.3828 14.2176C11.7612 14.1792 12.1399 14.1338 12.5195 14.0906C10.6351 11.8777 8.84953 9.62974 6.87598 7.53107C6.63127 7.30049 6.39023 7.05686 6.1543 6.81915C5.91229 6.57532 5.67346 6.33702 5.42773 6.11212C4.9842 5.70619 4.53231 5.35886 4.03223 5.12677C3.8501 5.20213 3.65616 5.19847 3.49414 5.14435C3.27448 5.07093 3.04436 4.88355 2.98926 4.59747V4.5965C2.94081 4.34909 3.04378 4.11906 3.22949 3.95294L3.23828 3.94513C3.59471 3.64562 4.03959 3.63778 4.45898 3.76056C4.87328 3.88192 5.32205 4.14449 5.7793 4.48224C6.69824 5.16102 7.7532 6.22143 8.79297 7.37384C9.83636 8.53027 10.8806 9.79772 11.7783 10.9012C12.3658 11.6233 12.8878 12.2687 13.3066 12.7723C13.3574 12.1301 13.4096 11.486 13.4658 10.8406L13.4668 10.8328C13.7943 7.79146 14.2509 4.70539 14.8857 1.57013L14.8867 1.56329C14.9998 1.04859 15.3476 0.639578 15.7988 0.624817Z"
                        fill="#E60000"
                        stroke="#E60000"
                        strokeWidth="0.75"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_8500_40821">
                        <rect width="27" height="31" fill="white"/>
                      </clipPath>
                    </defs>
                  </svg>
                </span>
              </>
            }
            isOpen={openSections.impact || false}
            onToggle={() => toggleSection('impact')}
          >
            <p className="body-default">
              We work together to drive meaningful results, taking pride in work with real value and purpose, through programs like Impact+, our sustainable development strategy.
            </p>
          </MobileAccordion>
        </div>
      </div>
    </div>
  );
}

